import React from 'react';
import { BedDouble, AlertTriangle } from 'lucide-react';
import FormField, { fieldClass } from './FormField';

/**
 * Room-category picker.
 *
 * `categories` MUST already be scoped to the station being booked. Listing every station's
 * categories was the cause of admins picking a room type that does not exist at their
 * station — the booking would then fail somewhere further down with an unhelpful message.
 */
const CategorySelect = ({
  categories,
  value,
  onChange,
  durationSlot,
  error,
  disabled = false,
  stationLabel,
  showAvailability = false,
  id = 'category'
}) => {
  const empty = categories.length === 0;

  return (
    <FormField
      label={<><BedDouble className="w-4 h-4 inline mr-1" />Room Category</>}
      htmlFor={id}
      required
      error={error}
      hint={stationLabel ? `Showing only the categories available at ${stationLabel}` : undefined}
    >
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled || empty}
        className={fieldClass(error, 'py-2.5')}
      >
        <option value="">{empty ? 'No categories at this station' : 'Select a room category'}</option>
        {categories.map((category) => {
          const price = category.pricing?.[durationSlot] ?? category.basePrice;
          const availability = showAvailability && category.availableRooms !== undefined
            ? ` · ${category.availableRooms} available`
            : '';
          return (
            <option key={category._id} value={category._id}>
              {category.name} — ₹{price}{availability}
            </option>
          );
        })}
      </select>

      {empty && (
        <p className="mt-1.5 flex items-start gap-1.5 text-xs text-amber-600">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
          <span>
            This station has no active room categories yet. A super admin needs to add them
            under Room Categories before bookings can be taken here.
          </span>
        </p>
      )}
    </FormField>
  );
};

export default CategorySelect;
